import { createSelector, createSlice } from '@reduxjs/toolkit';

import { SlicePayload, UntypedObject } from '@/types';
import { ESpotModel } from '@/models';
import { getLastUserStorageOrder } from '@/redux/thunks/checkout/getLastUserStorageOrder';
import { RootState } from '@/redux/create-store';

export type StorageState = {
  storage: UntypedObject;
  storageForWB: UntypedObject;
  storageHistoryList: UntypedObject[];
  cartStorage: UntypedObject[];
  cartStorageQtTotal: number;
  lastStorageProductAddedToCart: UntypedObject;
  cartStorageTunnelModalVisible: boolean;
  lastStorageOrder?: UntypedObject;
  storageWineBox: {
    storageWBId?: string;
    nbItems?: number;
    isComplete: boolean;
    products: UntypedObject[];
  },
  wbItemsModalVisible: boolean;
  storageItemsWB: UntypedObject[];
  storageProductDetailModalVisible: boolean;
  storageProductDetails: UntypedObject;
  storageProductDetailsOpenedFromWineBox: boolean;
  storageFilterModalVisible: boolean;
  storageProductsSelected: UntypedObject[];
  dashboardStats: UntypedObject;
  youSignInfosModalVisible: boolean;
  youSignEspot: ESpotModel;
}

export const initialState: StorageState = {
  storage: {},
  storageForWB: {},
  storageHistoryList: [],
  cartStorage: [],
  cartStorageQtTotal: 0,
  lastStorageProductAddedToCart: {},
  cartStorageTunnelModalVisible: false,
  lastStorageOrder: null,
  storageWineBox: {
    storageWBId: null,
    nbItems: null,
    isComplete: false,
    products: []
  },
  wbItemsModalVisible: false,
  storageItemsWB: [],
  storageProductDetailModalVisible: false,
  storageProductDetails: {},
  storageProductDetailsOpenedFromWineBox: false,
  storageFilterModalVisible: false,
  storageProductsSelected: [],
  dashboardStats: {},
  youSignInfosModalVisible: false,
  youSignEspot: undefined
};

const storage = createSlice({
  name: 'storage',
  initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getStorage: (_state, _action: SlicePayload<{ filter: string }>) => {
    },

    getStorageSuccess: (state, action: SlicePayload<{ storage: UntypedObject }>) => {
      state.storage = action.payload.storage;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getStorageError: (_state, _action: SlicePayload<{ error: any }>) => {
      // TODO TYPESCRIPT : ne sert à rien
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getStorageForWB: (_state, _action: SlicePayload<{ filter: string }>) => {
    },

    getStorageForWBSuccess: (state, action: SlicePayload<{ itemsList: UntypedObject }>) => {
      state.storageForWB = action.payload.itemsList;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getStorageHistory: (_state, _action: SlicePayload<{ status: string }>) => {
    },

    getStorageHistorySuccess: (state, action: SlicePayload<{ storageHistoryList: UntypedObject[] }>) => {
      state.storageHistoryList = action.payload.storageHistoryList;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getCartStorage: (_state) => {
    },

    getCartStorageSuccess: (state, action: SlicePayload<{ cartStorage: UntypedObject[] }>) => {
      state.cartStorage = action.payload.cartStorage;
      state.cartStorageQtTotal = action.payload.cartStorage?.length;
      state.lastStorageProductAddedToCart = undefined;
      state.cartStorageTunnelModalVisible = false;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCartStorage: (_state, _action: SlicePayload<{ cartStorageItems }>) => {
    },

    updateCartStorageSuccess: (
      state,
      action: SlicePayload<{
        cartStorage: UntypedObject[], lastStorageProductAddedToCart?: UntypedObject
      }>
    ) => {
      state.cartStorage = action.payload.cartStorage;
      state.cartStorageQtTotal = action.payload.cartStorage?.length;
      state.lastStorageProductAddedToCart = action.payload.lastStorageProductAddedToCart;
      state.cartStorageTunnelModalVisible = false;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCartStorageError: (_state, _action: SlicePayload<{ error: any }>) => {
      // TODO TYPESCRIPT : ne sert à rien
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    deleteCartStorage: (_state, _action: SlicePayload<{ cartStorageItems: UntypedObject[] }>) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    deleteCartStorageError: (_state, _action: SlicePayload<{ error: any }>) => {
      // TODO TYPESCRIPT : ne sert à rien
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clearLastStorageProductAddedToCart: (_state) => {
      // TODO TYPESCRIPT : ne sert à rien
    },

    toggleAddToCartStorageTunnelModal: (
      state,
      action: SlicePayload<{
        show: boolean, storageProductsSelected: UntypedObject[]
      }>
    ) => {
      const { show, storageProductsSelected } = action.payload;
      state.storageProductsSelected = storageProductsSelected;
      state.cartStorageTunnelModalVisible = show;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getLastStorageOrder: (_state, _action: SlicePayload<{ orderId: string }>) => {
    },

    getLastStorageOrderSuccess: (state, action: SlicePayload<{ lastStorageOrder: UntypedObject }>) => {
      state.lastStorageOrder = action.payload.lastStorageOrder;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    addItemToStorageWB: (_state, _action: SlicePayload<{ itemId: string, quantity: number }>) => {
    },

    addItemToStorageWBOk: (state, action: SlicePayload<{ storageWineBox: UntypedObject }>) => {
      const { storageWineBox } = action.payload;
      state.storageWineBox.storageWBId = storageWineBox.id;
      state.storageWineBox.nbItems = storageWineBox.nbItem;
      state.storageWineBox.isComplete = storageWineBox.complete;
      state.storageWineBox.products = storageWineBox.storageProductsForCart;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    triggerDeleteStorageWB: (_state, _action: SlicePayload<{ wineBoxStorageId: string }>) => {
    },

    deleteStorageWBSuccessThenResetFromStore: (state) => {
      state.storageWineBox.storageWBId = null;
      state.storageWineBox.nbItems = null;
      state.storageWineBox.isComplete = false;
      state.storageWineBox.products = [];
    },

    toggleShowWBItemsModal: (state, action: SlicePayload<{ show: boolean }>) => {
      state.wbItemsModalVisible = action.payload.show;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    triggerInsertStorageWineboxDetailsIntoStore: (_state, _action: SlicePayload<{ winebox: UntypedObject }>) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    triggerRemoveStorageWineBoxDetailsFromStore: (_state) => {
    },

    setStorageWineBoxDetails: (state, action: SlicePayload<{ wbItems: UntypedObject[] }>) => {
      state.storageItemsWB = action.payload.wbItems;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    showStorageProductDetailModal: (_state, _action: SlicePayload<{ storageProduct: UntypedObject, fromWineBox: boolean }>) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    hideStorageProductDetailModal: () => {
    },

    toggleStorageProductDetailModal: (
      state,
      action: SlicePayload<{
        show: boolean, storageProduct: UntypedObject, fromWineBox: boolean
      }>
    ) => {
      const { show, storageProduct, fromWineBox } = action.payload;
      state.storageProductDetailsOpenedFromWineBox = fromWineBox;
      state.storageProductDetailModalVisible = show;
      state.storageProductDetails = storageProduct;
    },

    toggleStorageFilterModal: (state, action: SlicePayload<{ show: boolean }>) => {
      state.storageFilterModalVisible = action.payload.show;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    showStorageFilterModal: () => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    hideStorageFilterModal: () => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    addStorageWineBoxToCart: (_state, _action: SlicePayload<{ wineBoxStorageId: string }>) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getDashboardStats: () => {
    },

    setDashboardStats: (state, action: SlicePayload<{ stats: UntypedObject }>) => {
      state.dashboardStats = action.payload.stats;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    resetCartStorage: () => {
    },

    toggleYouSignInfosModal: (state, action: SlicePayload<{ opened: boolean }>) => {
      state.youSignInfosModalVisible = action.payload.opened;
    },

    setYouSignEspot: (state, action: SlicePayload<{ espot: ESpotModel }>) => {
      state.youSignEspot = action.payload.espot;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    sendSignatureContract: (_state, _action: SlicePayload<{ pickId: string, t: (_k: string) => string }>) => {
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getLastUserStorageOrder.fulfilled, (state, action) => {
      state.lastStorageOrder = action.payload.order;
    });
  }
});

export const {
  getStorage, getStorageSuccess, getStorageError,
  getStorageForWB, getStorageForWBSuccess,
  getStorageHistory, getStorageHistorySuccess,
  getCartStorage, getCartStorageSuccess,
  updateCartStorage, updateCartStorageSuccess, updateCartStorageError,
  deleteCartStorage, deleteCartStorageError,
  clearLastStorageProductAddedToCart,
  toggleAddToCartStorageTunnelModal,
  getLastStorageOrder, getLastStorageOrderSuccess,
  addItemToStorageWB, addItemToStorageWBOk,
  triggerDeleteStorageWB,
  deleteStorageWBSuccessThenResetFromStore, toggleShowWBItemsModal,
  triggerInsertStorageWineboxDetailsIntoStore,
  triggerRemoveStorageWineBoxDetailsFromStore,
  setStorageWineBoxDetails,
  showStorageProductDetailModal, hideStorageProductDetailModal,
  toggleStorageProductDetailModal,
  toggleStorageFilterModal,
  showStorageFilterModal, hideStorageFilterModal,
  addStorageWineBoxToCart,
  getDashboardStats, setDashboardStats,
  resetCartStorage,
  toggleYouSignInfosModal, setYouSignEspot,
  sendSignatureContract
} = storage.actions;

export default storage.reducer;

const selectStorageState = (state: RootState) => state.storage;

export const selectCartStorage = createSelector(
  [selectStorageState],
  (storage) => storage.cartStorage
);
